import * as React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { CheckIcon, DotFilledIcon } from '@radix-ui/react-icons';
import { NavArrowRight } from 'iconoir-react';

import { useIsMobile } from 'hooks/use-mobile';
import { cn } from '../../lib/utils';

/** Figma Klax context menu — panel (node 1327:7077) */
const dropdownMenuPanelClass =
  'flex min-w-[200px] w-max max-w-[min(250px,calc(100vw-2rem))] flex-col items-stretch overflow-hidden rounded-lg border border-[#e7e7e7] bg-[#fff] p-1 text-popover-foreground shadow-[0_10px_20px_0_rgba(173,174,163,0.27)] dark:border-border dark:bg-popover dark:shadow-md';

const dropdownMenuItemTypographyClass =
  ' text-sm font-normal leading-[1.2] text-[#3D3D3D] dark:text-[#ffffff]';

const dropdownMenuIconClass =
  '[&>svg]:text-[#3D3D3D] dark:[&>svg]:text-[#ffffff]';

const dropdownMenuItemHoverClass =
  'data-[highlighted]:bg-[#F3F3F3] data-[highlighted]:text-[#3D3D3D] data-[highlighted]:[&>svg]:text-[#3D3D3D] focus:bg-[#F3F3F3] focus:text-[#3D3D3D] focus:[&>svg]:text-[#3D3D3D] dark:data-[highlighted]:bg-[#27272c] dark:data-[highlighted]:text-[#fff] dark:data-[highlighted]:[&>svg]:text-[#fff] dark:focus:bg-[#27272c] dark:focus:text-[#fff] dark:focus:[&>svg]:text-[#fff]';

/** Figma Error/600 — delete & leave actions only */
const dropdownMenuItemDestructiveClass =
  'text-[#d92d20] [&>svg]:!text-[#d92d20] data-[highlighted]:bg-[#F3F3F3] data-[highlighted]:text-[#d92d20] data-[highlighted]:[&>svg]:!text-[#d92d20] focus:bg-[#F3F3F3] focus:text-[#d92d20] focus:[&>svg]:!text-[#d92d20] dark:text-[#d92d20] dark:[&>svg]:!text-[#d92d20] dark:data-[highlighted]:bg-[#27272c] dark:data-[highlighted]:text-[#d92d20] dark:data-[highlighted]:[&>svg]:!text-[#d92d20] dark:focus:bg-[#27272c] dark:focus:text-[#d92d20] dark:focus:[&>svg]:!text-[#d92d20]';

const dropdownMenuItemClass =
  'relative flex h-[38px] w-full min-w-0 cursor-pointer select-none items-center gap-1.5 overflow-hidden rounded-lg pl-2 pr-1 outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:size-5 [&>svg]:shrink-0 [&>span]:min-w-0 [&>span]:flex-1 [&>span]:truncate';

const dropdownMenuSubTriggerClass =
  'relative flex h-[38px] w-full min-w-0 cursor-default select-none items-center gap-1.5 overflow-hidden rounded-lg pl-2 pr-1 outline-none transition-colors data-[state=open]:bg-[#F3F3F3] data-[state=open]:text-[#3D3D3D] data-[state=open]:[&>svg]:text-[#3D3D3D] dark:data-[state=open]:bg-[#27272c] dark:data-[state=open]:text-[#fff] dark:data-[state=open]:[&>svg]:text-[#fff] data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:size-5 [&>svg]:shrink-0 [&>span]:min-w-0 [&>span]:flex-1 [&>span]:truncate [&>svg:last-child]:ml-auto [&>svg:last-child]:size-4';

const dropdownMenuAnimationClass =
  'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2';

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

type MobileSubmenuContextValue = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const MobileSubmenuContext = React.createContext<MobileSubmenuContextValue | null>(null);

const DropdownMenuSub = ({
  children,
  open,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Sub>) => {
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  if (!isMobile) {
    return (
      <DropdownMenuPrimitive.Sub
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        {...props}
      >
        {children}
      </DropdownMenuPrimitive.Sub>
    );
  }

  return (
    <MobileSubmenuContext.Provider value={{ open: mobileOpen, setOpen: setMobileOpen }}>
      {children}
    </MobileSubmenuContext.Provider>
  );
};
DropdownMenuSub.displayName = DropdownMenuPrimitive.Sub.displayName;

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean;
  }
>(({ className, inset, children, disabled, onSelect, onClick, ...props }, ref) => {
  const mobileSubmenu = React.useContext(MobileSubmenuContext);

  if (mobileSubmenu) {
    return (
      <DropdownMenuPrimitive.Item
        ref={ref as React.Ref<React.ElementRef<typeof DropdownMenuPrimitive.Item>>}
        disabled={disabled}
        onClick={onClick}
        className={cn(
          dropdownMenuItemTypographyClass,
          dropdownMenuIconClass,
          dropdownMenuItemHoverClass,
          dropdownMenuSubTriggerClass,
          inset && 'pl-8',
          mobileSubmenu.open && 'bg-[#F3F3F3] dark:bg-[#27272c]',
          className
        )}
        onSelect={(event) => {
          event.preventDefault();
          if (disabled) return;
          mobileSubmenu.setOpen((isOpen) => !isOpen);
        }}
      >
        {children}
        <NavArrowRight
          width={16}
          height={16}
          strokeWidth={1.5}
          className={cn(
            'transition-transform duration-300 ease-out motion-reduce:transition-none',
            mobileSubmenu.open && 'rotate-90'
          )}
        />
      </DropdownMenuPrimitive.Item>
    );
  }

  return (
    <DropdownMenuPrimitive.SubTrigger
      ref={ref}
      disabled={disabled}
      onSelect={onSelect}
      onClick={onClick}
      className={cn(
        dropdownMenuItemTypographyClass,
        dropdownMenuIconClass,
        dropdownMenuItemHoverClass,
        dropdownMenuSubTriggerClass,
        inset && 'pl-8',
        className
      )}
      {...props}
    >
      {children}
      <NavArrowRight width={16} height={16} strokeWidth={1.5} />
    </DropdownMenuPrimitive.SubTrigger>
  );
});
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent> & {
    container?: HTMLElement | null;
  }
>(({ className, children, container, ...props }, ref) => {
  const mobileSubmenu = React.useContext(MobileSubmenuContext);

  if (mobileSubmenu) {
    return (
      <div
        className={cn(
          'grid overflow-hidden transition-[grid-template-rows,opacity,margin] duration-300 ease-out motion-reduce:transition-none',
          mobileSubmenu.open
            ? 'my-0.5 grid-rows-[1fr] opacity-100'
            : 'my-0 grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div
            ref={ref as React.Ref<HTMLDivElement>}
            role="group"
            data-mobile-submenu=""
            data-state={mobileSubmenu.open ? 'open' : 'closed'}
            aria-hidden={!mobileSubmenu.open}
            className={cn(
              className,
              'flex w-full flex-col items-stretch rounded-md bg-[#F3F3F3] p-0.5 dark:bg-[#27272c]',
              !mobileSubmenu.open && 'pointer-events-none',
              // Stronger than item hover so it reads on the gray submenu panel
              '[&_[role=menuitem][data-highlighted]]:!bg-[#fff] [&_[role=menuitem]]:focus:!bg-[#fff]',
              'dark:[&_[role=menuitem][data-highlighted]]:!bg-[#3a3a42] dark:[&_[role=menuitem]]:focus:!bg-[#3a3a42]'
            )}
          >
            {children}
          </div>
        </div>
      </div>
    );
  }

  const content = (
    <DropdownMenuPrimitive.SubContent
      ref={ref}
      className={cn('z-50', dropdownMenuPanelClass, dropdownMenuAnimationClass, className)}
      {...props}
    >
      {children}
    </DropdownMenuPrimitive.SubContent>
  );

  if (!container) {
    return content;
  }

  return (
    <DropdownMenuPrimitive.Portal container={container}>{content}</DropdownMenuPrimitive.Portal>
  );
});
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
    container?: HTMLElement | null;
  }
>(({ className, sideOffset = 4, container, onClickCapture, onPointerUpCapture, ...props }, ref) => {
  const [blockOpeningPointer, setBlockOpeningPointer] = React.useState(true);

  React.useLayoutEffect(() => {
    const release = () => {
      queueMicrotask(() => setBlockOpeningPointer(false));
    };
    window.addEventListener('pointerup', release, { once: true, capture: true });
    window.addEventListener('pointercancel', release, { once: true, capture: true });
    window.addEventListener('keyup', release, { once: true, capture: true });
    const timeout = window.setTimeout(() => setBlockOpeningPointer(false), 400);
    return () => {
      window.removeEventListener('pointerup', release, { capture: true });
      window.removeEventListener('pointercancel', release, { capture: true });
      window.removeEventListener('keyup', release, { capture: true });
      window.clearTimeout(timeout);
    };
  }, []);

  return (
    <DropdownMenuPrimitive.Portal container={container ?? undefined}>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn('z-50', dropdownMenuPanelClass, dropdownMenuAnimationClass, className)}
        {...props}
        onPointerUpCapture={(event) => {
          if (blockOpeningPointer) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          onPointerUpCapture?.(event);
        }}
        onClickCapture={(event) => {
          if (blockOpeningPointer) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          onClickCapture?.(event);
        }}
      />
    </DropdownMenuPrimitive.Portal>
  );
});
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      dropdownMenuItemTypographyClass,
      dropdownMenuIconClass,
      dropdownMenuItemHoverClass,
      dropdownMenuItemClass,
      inset && 'pl-8',
      className
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      dropdownMenuItemTypographyClass,
      dropdownMenuIconClass,
      dropdownMenuItemHoverClass,
      dropdownMenuItemClass,
      'cursor-default pl-8',
      className
    )}
    checked={checked}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <CheckIcon className="h-4 w-4" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      dropdownMenuItemTypographyClass,
      dropdownMenuIconClass,
      dropdownMenuItemHoverClass,
      dropdownMenuItemClass,
      'cursor-default pl-8',
      className
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <DotFilledIcon className="h-4 w-4 fill-current" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn('px-2 py-1.5 text-sm font-semibold', inset && 'pl-8', className)}
    {...props}
  />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('my-0 h-px w-full bg-[#e7e7e7] dark:bg-border', className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span className={cn('ml-auto text-xs tracking-widest opacity-60', className)} {...props} />
  );
};
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
};

export {
  dropdownMenuPanelClass,
  dropdownMenuItemClass,
  dropdownMenuItemTypographyClass,
  dropdownMenuSubTriggerClass,
  dropdownMenuIconClass,
  dropdownMenuItemHoverClass,
  dropdownMenuItemDestructiveClass,
};
